import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { API_BASE } from './apiConfig';
import { useLinkedPlayers } from './api/useLinkedPlayers';
import { useLiveList } from './SocketContext';
import {
    Banner, Button, DataTable, DotLabel, EmptyState, ErrorState, SearchField, Segmented, Skel, Tag,
} from './components/ui';
import './PlayersPage.css';

const toMs = (x) => {
    if (x == null || x === '') return null;
    if (typeof x === 'number') return x;
    const n = Number(x);
    if (Number.isFinite(n)) return n;
    const p = Date.parse(x);
    return Number.isFinite(p) ? p : null;
};
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
function fmtWhen(ms) {
    if (!ms) return '—';
    const d = new Date(ms);
    const now = new Date();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (sameDay(d, now)) return `Today, ${time}`;
    const y = new Date(now);
    y.setDate(now.getDate() - 1);
    if (sameDay(d, y)) return `Yesterday, ${time}`;
    return d.toLocaleDateString([], d.getFullYear() === now.getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
}
const fmtMonth = (ms) => (ms ? new Date(ms).toLocaleDateString([], { month: 'short', year: 'numeric' }) : '—');
const LINK_TAG = { sponsorship: 'Sponsored', data: 'Data sharing' };
const DAY = 864e5;

/** GET /api/sessions — 200 array | 204 empty body (never res.json() a 204). */
async function fetchSessions(authFetch, url) {
    const res = await authFetch(url);
    if (res.status === 204) return [];
    if (!res.ok) throw new Error(`Sessions didn’t load (${res.status})`);
    const text = await res.text();
    if (!text.trim()) return [];
    const data = JSON.parse(text);
    return Array.isArray(data) ? data : [];
}

function PlayersPage() {
    const { authFetch, user } = useAuth();
    const navigate = useNavigate();
    const { players, loading, error, reload } = useLinkedPlayers();
    const live = useLiveList();
    const [q, setQ] = useState('');
    const [filter, setFilter] = useState('all');

    // One request: a provider without user_id gets every verified-linked player's sessions.
    const [sess, setSess] = useState({ list: [], loading: true, error: null });
    const loadSessions = useCallback(async () => {
        setSess((s) => ({ ...s, loading: true, error: null }));
        try {
            const list = await fetchSessions(authFetch, `${API_BASE}/api/sessions`);
            setSess({ list, loading: false, error: null });
        } catch (e) {
            setSess({ list: [], loading: false, error: e.message || 'Sessions didn’t load' });
        }
    }, [authFetch]);
    useEffect(() => { if (user?.user_id) loadSessions(); }, [user, loadSessions]);

    // One row per player; a player can hold a data link and a sponsorship link.
    const merged = useMemo(() => {
        const m = new Map();
        players.forEach((p) => {
            const cur = m.get(p.user_id);
            if (!cur) m.set(p.user_id, { ...p, types: [p.link_type], allVerified: p.verified });
            else {
                if (!cur.types.includes(p.link_type)) cur.types.push(p.link_type);
                cur.allVerified = cur.allVerified && p.verified;
                if (!cur.since && p.since) cur.since = p.since;
            }
        });
        return [...m.values()];
    }, [players]);

    const stats = useMemo(() => {
        const cutoff = Date.now() - 30 * DAY;
        const m = {};
        sess.list.forEach((s) => {
            const t = toMs(s.startTime);
            if (!s.user_id || !t) return;
            const e = m[s.user_id] || (m[s.user_id] = { last: 0, n30: 0 });
            if (t > e.last) e.last = t;
            if (t >= cutoff) e.n30 += 1;
        });
        return m;
    }, [sess.list]);

    const liveBy = useMemo(() => {
        const m = {};
        live.sessions.forEach((s) => { if (s.userId && !m[s.userId]) m[s.userId] = s.sessionId; });
        return m;
    }, [live.sessions]);

    const counts = {
        all: merged.length,
        verified: merged.filter((p) => p.allVerified).length,
        unverified: merged.filter((p) => !p.allVerified).length,
        live: merged.filter((p) => liveBy[p.user_id]).length,
    };

    const needle = q.trim().toLowerCase();
    const visible = merged
        .filter((p) => filter === 'all'
            || (filter === 'verified' && p.allVerified)
            || (filter === 'unverified' && !p.allVerified)
            || (filter === 'live' && liveBy[p.user_id]))
        .filter((p) => !needle || `${p.name} ${p.email}`.toLowerCase().includes(needle))
        .sort((a, b) => (liveBy[b.user_id] ? 1 : 0) - (liveBy[a.user_id] ? 1 : 0) || (a.name || '').localeCompare(b.name || ''));

    const stop = (e) => e.stopPropagation();

    const columns = [
        {
            key: 'name', label: 'Player', w: 'minmax(200px,2fr)',
            render: (r) => (
                <span className="pls-who">
                    <span className="pls-avatar" aria-hidden="true">
                        {r.initial}
                        {r.liveId ? <span className="pls-avatar__live" /> : null}
                    </span>
                    <span className="pls-who__text">
                        <span className="pls-who__name">{r.name}</span>
                        {r.email ? <span className="pls-who__email">{r.email}</span> : null}
                    </span>
                </span>
            ),
        },
        {
            key: 'types', label: 'Link', w: 'minmax(0,1.2fr)',
            render: (r) => (
                <span className="pls-tags">
                    {r.types.map((t) => <Tag key={t} small accent={t === 'sponsorship'}>{LINK_TAG[t] || t}</Tag>)}
                </span>
            ),
        },
        { key: 'joined', label: 'Joined', w: 'minmax(96px,.7fr)', muted: true, hideOnMobile: true },
        {
            key: 'last', label: 'Last played', w: 'minmax(150px,1.1fr)',
            render: (r) => (sess.loading ? <Skel w={88} h={16} /> : <span className="eg-table__text">{r.last}</span>),
        },
        {
            key: 'n30', label: '30 days', w: 'minmax(96px,.5fr)', align: 'right',
            render: (r) => (sess.loading ? <Skel w={24} h={16} /> : <span className="eg-table__text eg-num" aria-label={`${r.n30} sessions in the last 30 days`}>{r.n30}</span>),
        },
        { key: 'status', label: 'Status', w: 'minmax(150px,1fr)', type: 'status' },
        {
            key: 'live', label: 'Live now', w: '164px', align: 'right',
            render: (r) => (r.liveId ? (
                <Button
                    variant="secondary"
                    to={`/live/${r.liveId}`}
                    className="pls-watch"
                    onClick={stop}
                    onKeyDown={stop}
                    aria-label={`Watch ${r.name} live`}
                >
                    <DotLabel color="var(--egdl-danger)" ink="var(--egdl-danger)" pulse>Watch live</DotLabel>
                </Button>
            ) : <span className="eg-table__text eg-table__text--muted">Not playing</span>),
        },
    ];

    const rows = visible.map((p) => {
        const st = stats[p.user_id];
        return {
            id: p.user_id,
            name: p.name,
            email: p.email,
            initial: (p.name || '?').trim().charAt(0).toUpperCase(),
            types: p.types,
            joined: fmtMonth(toMs(p.since)),
            last: st ? fmtWhen(st.last) : sess.error ? '—' : 'Never',
            n30: sess.error ? '—' : st ? st.n30 : 0,
            status: p.allVerified ? 'verified' : 'unverified',
            liveId: liveBy[p.user_id] || null,
            onClick: () => navigate(`/players/${p.user_id}`),
        };
    });

    const ready = !loading || merged.length > 0;
    const filters = [
        { value: 'all', label: `All ${counts.all}` },
        { value: 'verified', label: `Verified ${counts.verified}` },
        { value: 'unverified', label: `Awaiting you ${counts.unverified}` },
        { value: 'live', label: `Playing now ${counts.live}` },
    ];

    return (
        <div className="eg-page pls-page">
            <div className="pls-head">
                <h1 className="eg-h1">Players</h1>
                {ready && !error ? (
                    <span className="pls-count">
                        {counts.all} linked{live.loaded ? ` · ${counts.live} playing now` : ''}
                    </span>
                ) : null}
                <span className="eg-spacer" />
                <Button variant="primary" size="sm" icon="addPlayer" to="/sponsored-player">Add player</Button>
            </div>

            {error ? (
                <ErrorState title="Players didn’t load" kicker="" onRetry={reload} />
            ) : !ready ? (
                <div aria-busy="true" className="pls-loading">
                    <span className="eg-sr-only">Loading players…</span>
                    <Skel h={48} raised style={{ maxWidth: 420 }} />
                    <Skel h={44} raised /><Skel h={72} raised /><Skel h={72} raised /><Skel h={72} raised /><Skel h={72} raised />
                </div>
            ) : merged.length === 0 ? (
                <EmptyState
                    title="No linked players yet"
                    action={<Button variant="primary" size="sm" to="/sponsored-player">Add sponsored player</Button>}
                />
            ) : (
                <>
                    <div role="search" className="pls-tools">
                        <SearchField value={q} onChange={setQ} placeholder="Search name or email" label="Search players" style={{ flex: '1 1 280px', maxWidth: 420 }} />
                        <Segmented label="Filter by status" options={filters} value={filter} onChange={setFilter} />
                    </div>

                    {counts.unverified > 0 ? (
                        <Banner
                            tone="warning"
                            title={counts.unverified === 1 ? '1 link is waiting for you.' : `${counts.unverified} links are waiting for you.`}
                            action={<Button variant="secondary" to="/provider-profile">Review on Profile</Button>}
                        />
                    ) : null}

                    {sess.error ? (
                        <Banner tone="danger" title="Session history didn’t load." action={<Button variant="secondary" onClick={loadSessions}>Retry</Button>} />
                    ) : null}

                    {rows.length ? (
                        <DataTable caption="Linked players" columns={columns} rows={rows} getKey={(r) => r.id} />
                    ) : (
                        <div className="pls-nomatch">
                            <span>{needle ? `No players match “${q.trim()}”.` : 'No players in this view.'}</span>
                            <Button variant="ghost" onClick={() => { setQ(''); setFilter('all'); }}>{needle ? 'Clear search' : 'Show all'}</Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default PlayersPage;
